import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { CallLog } from '@adornis/voice-assistant/call-logs.js';
import { CallSession } from './CallSession.js';
import { LocalTicket } from './LocalTicket.js';
import { classifyCallResult } from '../server/call-result-policy.js';

/** Authenticated by the same global operation guard as /calls. No transcript leaves this query. */
export const getCallResults = registerQuery({
  type: () => String,
  operationName: 'getCallResults',
  params: [{ name: 'callIDs', type: () => [String] }],
  resolve: (callIDs: string[]) => async () => {
    if (
      !Array.isArray(callIDs) ||
      callIDs.length > 100 ||
      callIDs.some(id => typeof id !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(id))
    )
      throw new Error('Invalid call selection');
    const ids = [...new Set(callIDs)];
    if (!ids.length) return '{}';
    const [sessions, tickets, logs] = await Promise.all([
      getCollection(CallSession),
      getCollection(LocalTicket),
      getCollection(CallLog),
    ]);
    const [rows, created, messages] = await Promise.all([
      sessions
        .find(
          { sessionID: { $in: ids } },
          {
            projection: {
              sessionID: 1,
              callOrigin: 1,
              outcome: 1,
              endedAt: 1,
              errorReason: 1,
              resultActions: 1,
              _transferCheck: 1,
            },
          },
        )
        .toArray(),
      tickets
        .find(
          { sessionID: { $in: ids }, crmStatus: 'confirmed' },
          { projection: { sessionID: 1, crmStatus: 1, crmTicketNumber: 1 } },
        )
        .toArray(),
      logs
        // voice-provider-failure is emitted by our engine patch but absent from the upstream declaration.
        .find(
          {
            callID: { $in: ids },
            type: { $in: ['message', 'transcription', 'vad-real-start', 'voice-provider-failure'] },
          } as any,
          { projection: { callID: 1, type: 1, payload: 1, timestamp: 1 } },
        )
        .limit(20001)
        .toArray(),
    ]);
    // Truncated evidence is unavailable, never a fabricated failure/success.
    if (messages.length > 20000) throw new Error('Call result evidence exceeds batch limit');
    const result = Object.fromEntries(
      ids.map(id => {
        const events = messages.filter(m => m.callID === id);
        return [
          id,
          classifyCallResult({
            session: rows.find(s => s.sessionID === id),
            tickets: created.filter(t => t.sessionID === id),
            messages: events,
            lastActivity: Math.max(0, ...events.map(e => (e.timestamp ? new Date(e.timestamp).getTime() : 0) || 0)),
          }),
        ];
      }),
    );
    return JSON.stringify(result);
  },
});
